<template>
  <div v-if="error" class="alert alert-danger">
    <h5 class="alert-heading">{{ $t("js.backend.voter_importer.error") }}</h5>
    <details>
      <summary v-text="error.message" />
      <pre v-text="error.backtrace.join('\n')" />
    </details>
  </div>
  <div v-else>
    <p>
      <time :datetime="startedAt" v-if="startedAt">{{ $t("js.backend.voter_importer.started_time_ago", {
    time_ago:
      startedAgo
  }) }}</time>
      <span v-else>{{ $t("js.backend.voter_importer.starting_soon") }}</span>
    </p>
    <div class="progress mb-5">
      <div class="progress-bar" role="progressbar" :aria-valuenow="count" aria-valuemin="0" :aria-valuemax="currentTotal"
        :style="{ width: progress }">
        {{ `${count}/${currentTotal}` }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import apiClient from "@/entrypoints/frontend/apiClient";

export default defineComponent({
  name: "ImporterProgressBar",
  props: {
    importerId: {
      type: Number,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    initialCount: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 100,
    },
    startedAt: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      count: this.initialCount,
      currentTotal: this.total,
      reloading: false,
      intervalId: null,
      startedAgo: null,
      error: null,
    }
  },
  computed: {
    progress() {
      if (!this.currentTotal || this.currentTotal <= 0) return "0%";
      return `${Math.round(100 * this.count / this.currentTotal)}%`;
    },
    startedAtDateTime() {
      return Date.parse(this.startedAt);
    },
  },
  beforeMount() {
    this.relative_time = new Intl.RelativeTimeFormat(this.$i18n.locale || "da", { numeric: "auto" });
  },
  mounted() {
    this.checkStatus();
    this.updateTimestamp();
    this.intervalId = setInterval(this.checkStatus, 2000);
    setInterval(this.updateTimestamp, 1000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
  methods: {
    updateTimestamp() {
      if (!this.startedAtDateTime) return;
      const value = Math.round((this.startedAtDateTime - Date.now()) / 1000);
      if (Math.abs(value) >= 60) {
        this.startedAgo = this.relative_time.format(Math.floor(value / 60), "minute");
      } else {
        this.startedAgo = this.relative_time.format(value, "second");
      }
    },
    async checkStatus() {
      const response = await apiClient.get(window.location.href);
      const countFromStatus = response.data.counts?.[this.status];
      const progressCurrent = response.data.progressCurrent || 0;
      this.count = Math.max(countFromStatus || 0, progressCurrent);

      if (response.data.progressTotal !== undefined && response.data.progressTotal !== null) {
        this.currentTotal = response.data.progressTotal;
      }

      if (this.count > this.currentTotal) {
        this.currentTotal = this.count;
      }

      this.error = response.data.error;
      this.updateTimestamp();
      if (response.data.status && response.data.status !== this.status) {
        this.reloadPage();
        document.getElementById("cancel-button").classList.add("disabled");
      }
    },
    reloadPage() {
      window.clearInterval(this.intervalId);
      if (this.reloading) return;
      this.reloading = true;
      setTimeout(() => window.location.reload(), 500); // Small delay, so the user can see the progressbar hit 100%
    },
  },
});
</script>
